import { useEffect, useMemo, useState } from 'react';
import { ItemModalDraft, ItemModalErrors, validateItemDraft } from '../utils/itemValidation';

export type ItemType = 'tool' | 'part';

export interface EditableItem {
  id: string;
  name: string;
  code?: string;
  category?: string;
  imageUrl?: string;
  available?: boolean;
}

interface UseItemModalDraftOptions<T extends EditableItem> {
  item: T | null;
  itemType: ItemType;
  onSaved?: (item: T, itemType: ItemType) => void;
  uploadImage?: (itemType: ItemType, entityId: string, file: File) => Promise<string>;
  updateItem?: (
    itemType: ItemType,
    id: string,
    payload: {
      name: string;
      code?: string;
      category?: string;
      available: boolean;
      imageUrl?: string;
    },
  ) => Promise<Partial<T>>;
}

function buildDraft<T extends EditableItem>(item: T | null): ItemModalDraft {
  return {
    id: item?.id ?? '',
    name: item?.name ?? '',
    code: item?.code ?? '',
    category: item?.category ?? '',
    status: item?.available === false ? 'unavailable' : 'available',
    imageUrl: item?.imageUrl ?? null,
  };
}

export function useItemModalDraft<T extends EditableItem>({
  item,
  itemType,
  onSaved,
  uploadImage,
  updateItem,
}: UseItemModalDraftOptions<T>) {
  const [draft, setDraft] = useState<ItemModalDraft>(() => buildDraft(item));
  const [uploadFile, setUploadFileState] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<ItemModalErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    setDraft(buildDraft(item));
    setUploadFileState(null);
    setPreviewUrl((currentPreviewUrl) => {
      if (currentPreviewUrl) {
        URL.revokeObjectURL(currentPreviewUrl);
      }
      return null;
    });
    setErrors({});
    setSaveMessage(null);
  }, [item]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const setField = <K extends keyof ItemModalDraft>(key: K, value: ItemModalDraft[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    setSaveMessage(null);
  };

  const setUploadFile = (file: File | null) => {
    if (file) {
      const imageValidationErrors = validateItemDraft(draft, file);
      if (imageValidationErrors.image) {
        setUploadFileState(null);
        setPreviewUrl((currentPreviewUrl) => {
          if (currentPreviewUrl) {
            URL.revokeObjectURL(currentPreviewUrl);
          }
          return null;
        });
        setErrors((prev) => ({ ...prev, image: imageValidationErrors.image }));
        setSaveMessage(null);
        return;
      }
    }

    setPreviewUrl((currentPreviewUrl) => {
      if (currentPreviewUrl) {
        URL.revokeObjectURL(currentPreviewUrl);
      }
      return file ? URL.createObjectURL(file) : null;
    });
    setUploadFileState(file);
    setErrors((prev) => ({ ...prev, image: undefined }));
    setSaveMessage(null);
  };

  const reset = () => {
    setDraft(buildDraft(item));
    setUploadFileState(null);
    setPreviewUrl((currentPreviewUrl) => {
      if (currentPreviewUrl) {
        URL.revokeObjectURL(currentPreviewUrl);
      }
      return null;
    });
    setErrors({});
    setSaveMessage(null);
  };

  const isDirty = useMemo(() => {
    if (!item) return false;

    return (
      draft.name !== item.name ||
      draft.id !== item.id ||
      draft.code !== (item.code ?? '') ||
      draft.category !== (item.category ?? '') ||
      draft.status !== (item.available === false ? 'unavailable' : 'available') ||
      uploadFile !== null
    );
  }, [draft, item, uploadFile]);

  const save = async () => {
    const nextErrors = validateItemDraft(draft, uploadFile);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0 || !item) {
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    try {
      let imageUrl = draft.imageUrl;
      if (uploadFile) {
        if (!uploadImage) {
          throw new Error('No upload handler provided.');
        }
        imageUrl = await uploadImage(
          itemType,
          item.id,
          uploadFile,
        );
      }

      if (!updateItem) {
        throw new Error('No update handler provided.');
      }
      const updatedFields = await updateItem(
        itemType,
        item.id,
        {
        name: draft.name,
        code: draft.code || undefined,
        category: draft.category || undefined,
        available: draft.status === 'available',
        imageUrl: imageUrl ?? undefined,
      });

      const updatedItem = {
        ...item,
        ...updatedFields,
        imageUrl: imageUrl ?? item.imageUrl,
        available: draft.status === 'available',
      } as T;

      onSaved?.(updatedItem, itemType);
      setDraft(buildDraft(updatedItem));
      setUploadFileState(null);
      setPreviewUrl((currentPreviewUrl) => {
        if (currentPreviewUrl) {
          URL.revokeObjectURL(currentPreviewUrl);
        }
        return null;
      });
      setErrors({});
      setSaveMessage('Changes saved.');
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        submit: error instanceof Error ? error.message : 'Unable to save changes.',
      }));
    } finally {
      setIsSaving(false);
    }
  };

  return {
    draft,
    setField,
    uploadFile,
    setUploadFile,
    imagePreviewUrl: previewUrl ?? draft.imageUrl,
    errors,
    isDirty,
    isSaving,
    save,
    reset,
    saveMessage,
  };
}
